<template>
  <!--
    Gemeinsames Layout für /user/home und /club/home im Stil der Startseite (component/welcome.vue).
    Die Views liefern nur fertige Daten, die Texte stehen in src/content/homePages.js.
  -->
  <div class="home">
    <section class="home-hero">
      <v-container class="home-wrap">
        <p class="home-kicker">{{ kicker }}</p>
        <h1 class="home-h1">{{ title }}</h1>
        <p class="home-lead">{{ lead }}</p>
        <div class="home-actions">
          <v-btn
            v-for="(action, index) in actions"
            :key="action.to"
            x-large
            :depressed="index === 0"
            :outlined="index > 0"
            :color="index === 0 ? 'success' : 'success darken-2'"
            :to="action.to"
          >
            {{ action.text }}
            <v-icon v-if="index === 0" right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <v-row class="home-stats">
          <v-col v-for="stat in stats" :key="stat.id" cols="6" md="3">
            <router-link :to="stat.to" class="home-stat" :class="{ 'home-stat--highlight': stat.highlight }">
              <span class="home-stat-value">{{ loading ? '–' : stat.value }}</span>
              <span class="home-stat-label">{{ stat.label }}</span>
              <span v-if="stat.caption" class="home-stat-caption">{{ stat.caption }}</span>
            </router-link>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section v-if="setup" class="home-setup">
      <v-container class="home-wrap">
        <div class="home-setup-card">
          <h2 class="home-h2">{{ setup.title }}</h2>
          <p class="home-text">{{ setup.text }}</p>
          <ol class="home-steps">
            <li v-for="(step, index) in setup.steps" :key="step.id" :class="{ 'home-step--done': step.done }">
              <span class="home-step-marker">
                <v-icon v-if="step.done" small color="white">mdi-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <div>
                <h3 class="home-step-title">
                  {{ step.title }}
                  <span v-if="step.optional" class="home-step-optional">optional</span>
                </h3>
                <p class="home-step-text">{{ step.text }}</p>
                <router-link v-if="!step.done" :to="step.link.to" class="home-link">
                  {{ step.link.text }}<v-icon small color="success darken-2">mdi-chevron-right</v-icon>
                </router-link>
              </div>
            </li>
          </ol>
        </div>
      </v-container>
    </section>

    <section class="home-tiles">
      <v-container class="home-wrap">
        <h2 class="home-h2">{{ tilesTitle }}</h2>
        <v-row>
          <v-col v-for="tile in tiles" :key="tile.id" cols="12" sm="6" lg="4">
            <router-link :to="tile.to" class="home-tile">
              <span class="home-tile-icon">
                <v-icon color="success darken-2">{{ tile.icon }}</v-icon>
              </span>
              <span>
                <span class="home-tile-title">{{ tile.title }}</span>
                <span class="home-tile-text">{{ tile.text }}</span>
              </span>
            </router-link>
          </v-col>
        </v-row>

        <p v-if="footnote" class="home-footnote">
          {{ footnote.text }}
          <router-link :to="footnote.link.to" class="home-link">{{ footnote.link.text }}</router-link>
        </p>
      </v-container>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomePage',

  props: {
    kicker: { type: String, default: '' },
    title: { type: String, required: true },
    lead: { type: String, default: '' },
    // [{ text, to }] – der erste Button ist der Hauptbutton
    actions: { type: Array, default: () => [] },
    // [{ id, value, label, caption?, to, highlight? }]
    stats: { type: Array, default: () => [] },
    // { title, text, steps: [{ id, title, text, link: { text, to }, done?, optional? }] } oder null
    setup: { type: Object, default: null },
    tilesTitle: { type: String, default: '' },
    // [{ id, icon, title, text, to }]
    tiles: { type: Array, default: () => [] },
    footnote: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  }
};
</script>

<style scoped>
/* Farben und Schriftgrößen wie auf der Startseite (component/welcome.vue) */
.home {
  --ink: #1d2a1f;
  --muted: #526255;
  --green: #2e7d32;
  --tint: #f3f8f1;
  --line: #e1e9dd;
  min-height: 100%;
  color: var(--ink);
  background: #fff;
}

.home-wrap {
  max-width: 1180px;
}

.home-hero {
  padding: 36px 0 28px;
  background: linear-gradient(180deg, #eaf5e5 0%, #fff 100%);
}

.home-kicker {
  margin-bottom: 0.75rem;
  color: var(--green);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.home-h1 {
  margin-bottom: 1rem;
  font-size: clamp(1.9rem, 3.6vw, 2.7rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.02em;
}

.home-lead {
  max-width: 40em;
  color: var(--muted);
  font-size: 1.15rem;
  line-height: 1.65;
}

.home-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.home-stats {
  margin-top: 28px;
}

.home-stat {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 18px 20px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fff;
  color: inherit !important;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.home-stat:hover {
  border-color: #b9d6ae;
  box-shadow: 0 10px 24px -18px rgba(20, 45, 20, 0.5);
}

.home-stat--highlight {
  border-color: #f0c36d;
  background: #fffaf0;
}

.home-stat-value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
}

.home-stat-label {
  margin-top: 4px;
  font-weight: 600;
}

.home-stat-caption {
  color: var(--muted);
  font-size: 0.85rem;
}

.home-h2 {
  margin-bottom: 1rem;
  font-size: clamp(1.4rem, 2.4vw, 1.9rem);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.home-text {
  max-width: 44em;
  color: var(--muted);
  font-size: 1.05rem;
  line-height: 1.7;
}

.home-link {
  display: inline-flex;
  align-items: center;
  color: var(--green) !important;
  font-weight: 600;
  text-decoration: none;
}

.home-link:hover {
  text-decoration: underline;
}

.home-setup {
  padding: 8px 0 24px;
}

.home-setup-card {
  padding: 28px;
  border-radius: 14px;
  background: var(--tint);
}

.home-steps {
  display: grid;
  gap: 20px;
  margin: 20px 0 0;
  padding: 0 !important;
  list-style: none;
}

.home-steps li {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.home-step-marker {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 2px solid var(--green);
  border-radius: 50%;
  color: var(--green);
  font-size: 0.9rem;
  font-weight: 700;
}

.home-step--done .home-step-marker {
  background: var(--green);
}

.home-step--done .home-step-title,
.home-step--done .home-step-text {
  color: #8a968c;
}

.home-step-title {
  margin: 2px 0 4px;
  font-size: 1.05rem;
  font-weight: 700;
}

.home-step-optional {
  margin-left: 6px;
  color: #8a968c;
  font-size: 0.8rem;
  font-weight: 400;
}

.home-step-text {
  margin: 0 0 4px;
  color: var(--muted);
  line-height: 1.6;
}

.home-tiles {
  padding: 32px 0 64px;
}

.home-tile {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  height: 100%;
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fff;
  color: inherit !important;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.home-tile:hover {
  border-color: #b9d6ae;
  box-shadow: 0 14px 30px -22px rgba(20, 45, 20, 0.55);
}

.home-tile-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--tint);
}

.home-tile-title {
  display: block;
  margin-bottom: 4px;
  font-size: 1.1rem;
  font-weight: 700;
}

.home-tile-text {
  display: block;
  color: var(--muted);
  line-height: 1.55;
}

.home-footnote {
  margin: 32px 0 0;
  color: var(--muted);
}

@media (min-width: 960px) {
  .home-hero {
    padding: 64px 0 40px;
  }
}
</style>
